<template>
  <div class="flex-center h-full">
    <div class="inline-flex items-center gap-1 py-1 px-1 rounded-md shadow-md bg-white dark:bg-charcoal-800 dark:ring-1 dark:ring-charcoal-600 pointer-events-auto">
      <component :is="elementComponent" v-if="selectedElement" />

      <template v-if="!selectedElement.background">
        <surface-editor-core-toolbar-divider />

        <u-popover>
          <core-tooltip text="Transparency">
            <u-button color="charcoal" variant="ghost" icon="i-carbon-opacity" class="dark:text-white" />
          </core-tooltip>
          <template #panel>
            <surface-editor-core-slider-input
              v-model="opacity"
              @drag-start="surfaceEditorStore.setSliderDragging(true)"
              @drag-end="surfaceEditorStore.setSliderDragging(false)"
              @commit-updates="surfaceEditorStore.commitUndo()"
              class="p-3"
            >
              <template #label>Transparency</template>
            </surface-editor-core-slider-input>
          </template>
        </u-popover>

        <surface-editor-core-toolbar-divider />

        <u-dropdown
          :items="positionItems"
          :popper="{ placement: 'bottom-start' }"
        >
          <core-tooltip text="Arrange position">
            <u-button color="charcoal" variant="ghost" class="dark:text-white">Position</u-button>
          </core-tooltip>
        </u-dropdown>

        <surface-editor-core-toolbar-divider />

        <core-tooltip text="Delete">
          <u-button color="charcoal" variant="ghost" :icon="COMMON_ICONS.delete" class="dark:text-white" @click="deleteElement" />
        </core-tooltip>

      </template>

    </div>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {
    SurfaceEditorElementToolbarBackground,
    SurfaceEditorElementToolbarImage,
    SurfaceEditorElementToolbarText,
    SurfaceEditorElementToolbarShape
  } from '#components';

  const surfaceEditorStore = useSurfaceEditorStore();
  const {selectedElement, surfaces, contextualPanel} = storeToRefs(surfaceEditorStore);

  const surfaceEditorUtils = useSurfaceEditorUtils();
  const elementInSurface = computed(() => surfaceEditorUtils.findElementInSurface({
    elementId: selectedElement.value.id,
    surfaces: surfaces.value
  }));

  const elementComponent = computed(() => {
    if (selectedElement.value.background) {
      return SurfaceEditorElementToolbarBackground;
    }

    switch (selectedElement.value?.type) {
      case SURFACE_EDITOR_ELEMENT_TYPE.image:
        return SurfaceEditorElementToolbarImage;
      case SURFACE_EDITOR_ELEMENT_TYPE.text:
        return SurfaceEditorElementToolbarText;
      case SURFACE_EDITOR_ELEMENT_TYPE.shape:
        return SurfaceEditorElementToolbarShape;
    }
  });

  const opacity = computed({
    get() {
      return parseInt(selectedElement.value.opacity * 100);
    },
    set(opacity) {
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          opacity: Math.round(opacity) / 100
        },
        commit: false
      });
    }
  });

  const overlaps = computed(() => surfaceEditorUtils.getOverlappingElements({
    element: selectedElement.value,
    surface: elementInSurface.value.surface
  }));
  const elementOverlaps = computed(() => {
    const selectedElementOverlapIndex = overlaps.value.findIndex(overlap => overlap.element.id === selectedElement.value.id);

    return {
      over: selectedElementOverlapIndex < overlaps.value.length - 1,
      under: selectedElementOverlapIndex > 0
    };
  });

  function reorder(action) {
    surfaceEditorStore.reorderElements({element: selectedElement.value, action});
  }


  const positionItems = computed(() => [
    [
      {
        label: 'Forward',
        icon: 'i-ri-bring-forward',
        disabled: !elementOverlaps.value.over,
        click: () => reorder(SURFACE_EDITOR_REORDER_ACTIONS.forward)
      },
      {
        label: 'Backward',
        icon: 'i-ri-send-backward',
        disabled: !elementOverlaps.value.under,
        click: () => reorder(SURFACE_EDITOR_REORDER_ACTIONS.backward)
      }
    ],
    [
      {
        label: 'To Front',
        icon: 'i-ri-bring-to-front',
        disabled: !elementOverlaps.value.over,
        click: () => reorder(SURFACE_EDITOR_REORDER_ACTIONS.front)
      },
      {
        label: 'To Back',
        icon: 'i-ri-send-to-back',
        disabled: !elementOverlaps.value.under,
        click: () => reorder(SURFACE_EDITOR_REORDER_ACTIONS.back)
      }
    ]
  ]);

  function deleteElement() {
    surfaceEditorStore.deleteElement({element: selectedElement.value, surface: elementInSurface.value.surface});
  }

  // Close contextual panel when switching to an element that doesn't support it
  watch(selectedElement, (newEl) => {
    if (contextualPanel.value?.id !== 'color') return;

    const mode = contextualPanel.value.props?.mode;
    const fillModes = [SURFACE_EDITOR_COLOR_PANEL_MODES.shapeFill, SURFACE_EDITOR_COLOR_PANEL_MODES.bgFill];
    const isFillPanel = fillModes.includes(mode);
    const isStrokePanel = mode === SURFACE_EDITOR_COLOR_PANEL_MODES.shapeStroke;
    const isTextPanel = mode === SURFACE_EDITOR_COLOR_PANEL_MODES.text;

    const supportsFill = (el) => el?.background || el?.type === SURFACE_EDITOR_ELEMENT_TYPE.shape;
    const isShape = (el) => el?.type === SURFACE_EDITOR_ELEMENT_TYPE.shape;
    const isText = (el) => el?.type === SURFACE_EDITOR_ELEMENT_TYPE.text;

    if (isFillPanel && !supportsFill(newEl)) {
      surfaceEditorStore.closeContextualPanel();
      return;
    }

    if (isStrokePanel && !isShape(newEl)) {
      surfaceEditorStore.closeContextualPanel();
      return;
    }

    if (isTextPanel && !isText(newEl)) {
      surfaceEditorStore.closeContextualPanel();
    }
  });

</script>
